import type {
  ListingApplicationContactInput,
  ListingApplicationContactQuery,
} from "@/graphql/generated/operations";
import { importFieldError, type ImportFieldError } from "@/lib/import-field-validation";

// Die Kontaktdaten einer Bewerbung. genesis schreibt sie auf die Person und füllt dabei NUR leere
// Felder; ein vorhandener Wert wird nie überschrieben, und die Bewerbung behält keine Kopie
// (@external-adr GENESIS-0087-application-phases-are-separate-from-decisions (GENESIS-0071 §2)).
//
// Der Dialog zeigt deshalb, was das Profil schon hat, als feste Angabe und bietet nur die leeren
// Felder zum Ausfüllen an. Ein änderbares Feld, dessen Eingabe genesis still verwirft, wäre eine
// Zusage, die niemand hält. Geprüft wird mit denselben Regeln wie im Profil-Editor.

export type ContactField =
  | "givenName"
  | "familyName"
  | "phone"
  | "website"
  | "street"
  | "houseNumber"
  | "zipCode"
  | "city"
  | "countryId";

export type ContactValues = Record<ContactField, string>;

export const EMPTY_CONTACT: ContactValues = {
  givenName: "",
  familyName: "",
  phone: "",
  website: "",
  street: "",
  houseNumber: "",
  zipCode: "",
  city: "",
  countryId: "",
};

/** Die Felder mit einer Formatregel im Profil-Editor (Länge, Telefon, Website). */
const CHECKED_FIELDS = ["phone", "website", "street", "houseNumber", "zipCode", "city"] as const;

type Myself = NonNullable<ListingApplicationContactQuery["myself"]>;

/** Was das Profil schon hat, als Feldwerte; leer heißt: der Dialog darf es füllen. */
export function profileContact(myself: Myself | null | undefined): ContactValues {
  if (!myself) return EMPTY_CONTACT;
  return {
    givenName: (myself.name?.given ?? []).map((given) => given.trim()).filter(Boolean).join(" "),
    familyName: myself.name?.family?.trim() ?? "",
    phone: myself.phone?.trim() ?? "",
    website: myself.website?.trim() ?? "",
    street: myself.street?.trim() ?? "",
    houseNumber: myself.houseNumber?.trim() ?? "",
    zipCode: myself.zipCode?.trim() ?? "",
    city: myself.city?.trim() ?? "",
    countryId: myself.country?.id ?? "",
  };
}

export function contactFieldLocked(profile: ContactValues, field: ContactField): boolean {
  return profile[field] !== "";
}

/** Die Formatfehler der ausfüllbaren Felder, nach den Regeln des Profil-Editors. */
export function contactErrors(
  values: ContactValues,
  profile: ContactValues,
): Partial<Record<ContactField, ImportFieldError>> {
  const errors: Partial<Record<ContactField, ImportFieldError>> = {};
  for (const field of CHECKED_FIELDS) {
    if (contactFieldLocked(profile, field)) continue;
    const error = importFieldError(field, values[field]);
    if (error) errors[field] = error;
  }
  return errors;
}

/**
 * Was an `applyToListing` geht: nur ausgefüllte Felder, die im Profil leer sind. Ohne ein einziges
 * davon null, dann bleibt `contact` ganz weg.
 */
export function contactInput(values: ContactValues, profile: ContactValues): ListingApplicationContactInput | null {
  const input: ListingApplicationContactInput = {};
  for (const field of Object.keys(EMPTY_CONTACT) as ContactField[]) {
    const value = values[field].trim();
    if (value && !contactFieldLocked(profile, field)) input[field] = value;
  }
  return Object.keys(input).length ? input : null;
}
